import { useEffect } from "react";
import { Link } from "react-router-dom";
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { rtctApi } from "services/rtctApi";
import { analysisPoints } from "utils/radarAnalysis";
import { fieldTime } from "utils/fieldFormat";
import { useStableState } from "utils/stableState";

// Water level on the node page: our estimate from the radar's last day of
// bursts (GET radar/analysis/?days=1, the Radar analysis page's own source),
// as depth against the soil when the node's profile knows the soil, else as
// the distance from the radar. The details live on Radar analysis.
const POLL_MS = 10 * 60 * 1000;

const CLOCK = new Intl.DateTimeFormat("en-GB", { timeZone: "Asia/Singapore", hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
// "09:51" (SGT), for the chart's time axis.
export const clockTime = (ms) => CLOCK.format(new Date(ms));

export function waterRows(data) {
  return analysisPoints(data)
    .filter((p) => p.depthMm !== null || p.ourMm !== null)
    .map((p) => ({
      ms: p.ms,
      t: p.t,
      depthCm: p.depthMm !== null ? Math.round(p.depthMm) / 10 : null,
      ourCm: p.ourMm !== null ? Math.round(p.ourMm) / 10 : null,
      state: p.state,
      belowTube: p.depthBelowTube,
    }));
}

export function levelText(row) {
  if (!row) return "No analysed reading in the last day";
  if (row.belowTube) return "The pipe is dry: the water is below it";
  if (row.depthCm !== null)
    return row.depthCm >= 0 ? `${row.depthCm.toFixed(1)} cm over the soil` : `${(-row.depthCm).toFixed(1)} cm under the soil`;
  return `${row.ourCm.toFixed(1)} cm from the radar`;
}

export default function WaterLevelCard({ mac }) {
  const [state, setState] = useStableState({ mac: "", rows: null, error: "" });
  useEffect(() => {
    if (!mac || typeof rtctApi.radarAnalysis !== "function") return undefined;
    let abort = new AbortController();
    const load = () => {
      abort.abort();
      abort = new AbortController();
      const signal = abort.signal;
      Promise.resolve(rtctApi.radarAnalysis(mac, signal, { days: 1, maxPoints: 288 }))
        .then((data) => {
          if (!signal.aborted) setState({ mac, rows: waterRows(data), error: "" });
        })
        .catch((e) => {
          if (!signal.aborted) setState((old) => ({ ...old, mac, error: e?.message || "Water level unavailable" }));
        });
    };
    load();
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, POLL_MS);
    return () => {
      clearInterval(timer);
      abort.abort();
    };
  }, [mac, setState]);
  const rows = state.mac === mac ? state.rows : null;
  const latest = rows?.length ? rows[rows.length - 1] : null;
  const useDepth = rows?.some((r) => r.depthCm !== null);
  return (
    <section className="field-panel" aria-label="Water level">
      <div className="field-panel-head">
        <div>
          <h2>Water level</h2>
          <p>Last 24 hours</p>
        </div>
        {latest?.state && <span className="field-state">{latest.state}</span>}
      </div>
      <div className="field-panel-body">
        {state.error && state.mac === mac && <div className="field-alert">{state.error}</div>}
        <p className="field-node-headline">{rows ? levelText(latest) : "Loading…"}</p>
        {latest && <p className="field-caption">At {fieldTime(latest.t)} SGT</p>}
        {rows && rows.length > 1 && (
          <div className="field-node-chart" aria-label="Water level, last 24 hours">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={rows} margin={{ top: 8, right: 20, bottom: 0, left: 0 }}>
                <CartesianGrid stroke="#e8eff1" vertical={false} />
                <XAxis
                  dataKey="ms"
                  type="number"
                  domain={["dataMin", "dataMax"]}
                  tickFormatter={clockTime}
                  tick={{ fontSize: 10 }}
                />
                <YAxis width={40} tick={{ fontSize: 10 }} unit=" cm" reversed={!useDepth} />
                <Tooltip labelFormatter={(ms) => `${fieldTime(new Date(ms).toISOString())} SGT`} />
                <Line
                  type="monotone"
                  dataKey={useDepth ? "depthCm" : "ourCm"}
                  name={useDepth ? "Over the soil (cm)" : "From the radar (cm)"}
                  stroke="#087f70"
                  dot={false}
                  isAnimationActive={false}
                  connectNulls
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
        <p className="field-caption">
          More in <Link to="/radar-analysis">Radar analysis</Link>.
        </p>
      </div>
    </section>
  );
}
